import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { UnauthorizedView } from "@/components/UnauthorizedView/UnauthorizedView";
import { ROUTES } from "@/routes";
import sharedStyles from "../UserManagement.module.css";
import styles from "./CustomerSupport.module.css";
import UserProfile from "./UserProfile";

// Same read grant as the list — the detail route is directly linkable, so it has to gate
// itself rather than inherit anything from the page the agent came from.
const WEBSITE_USERS_READ_PERMISSION = "staff.website_users.read";

/**
 * Route-driven website-user profile (`/users/website/:userId`).
 *
 * The userId comes from the URL, not from list state, so a pasted link cold-loads the
 * profile with nothing in memory.
 *
 * The band carries a fixed title rather than the person's name: the profile — with its loading,
 * error and not-found states — is fetched inside `UserProfile`, so a name here would have to
 * render as a placeholder on three of the four states the page can be in. The record itself is
 * identified by the email at the top of the profile.
 */
export default function WebsiteUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();

  return (
    <PermissionGate permission={WEBSITE_USERS_READ_PERMISSION} fallback={<UnauthorizedView />}>
      <div className={sharedStyles.wrapper}>
        {/* .wrapper pulls its first child out to the viewport edge, so PageHeader needs this
            plain div to absorb that — its own padding then reads as a full-bleed band. */}
        <div>
          <PageHeader
            title="Website user"
            subtitle="Customer support profile"
            meta={
              /* The meta slot renders inside the band's left-hand column, under the subtitle;
                 PageHeader's children render hard right, which is not where a back
                 affordance is looked for. */
              <Link className={styles.backLink} to={ROUTES.users.website}>
                {/* Decorative: the link text already says where it goes. */}
                <span aria-hidden="true">&larr;</span> Back to website users
              </Link>
            }
          />
        </div>

        {userId ? (
          <UserProfile userId={userId} />
        ) : (
          <EmptyState
            variant="error"
            title="Couldn't load profile"
            message="This URL is missing a user id."
          />
        )}
      </div>
    </PermissionGate>
  );
}
